import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { selectMessages } from './messages';
import { selectSellingShowings } from './showings';
import { selectSellingOffers } from './offers';
import { selectIsDemoMode, selectDemoListings } from './demo';
import _ from 'lodash';
import { isBefore, isAfter } from 'date-fns';
import { ListingStatus } from '@prisma/client';

export interface PreListingStatusData {
  listing: {
    pending: boolean;
    completed: boolean;
  };
  photos: {
    pending: boolean;
    completed: boolean;
  };
  price: {
    pending: boolean;
    completed: boolean;
    userPriced: boolean;
    homepointPriced: boolean;
    needsUserPricing: boolean;
  };
  signing: {
    pending: boolean;
    completed: boolean;
  };
  final: {
    pending: boolean;
    completed: boolean;
  };
}

export const selectUserId = (state: RootState) => state.auth.user?.id;
export const selectOrganizationId = (state: RootState) =>
  state.auth.user?.organizationId;

// Base listings selector
const selectBaseListings = (state: RootState) => state.listings.listings;

// Enhanced selector that includes demo data when in demo mode
export const selectListings = createSelector(
  [selectBaseListings, selectIsDemoMode, selectDemoListings],
  (baseListings, isDemoMode, demoListings) => {
    if (!isDemoMode) return baseListings;
    // Put demo listings first so they appear at the top
    return [...demoListings, ...baseListings];
  }
);

export const selectListingById = createSelector(
  [selectListings],
  (listings) => (id: string) => listings.find((listing) => listing.id === id)
);

export const selectOrgListings = createSelector(
  [selectListings, selectOrganizationId],
  (listings, organizationId) =>
    listings.filter(
      (listing) =>
        listing.organizationId === organizationId && organizationId !== null
    )
);

export const selectMyBuyingListings = createSelector(
  [selectListings, selectOrgListings, selectUserId, selectIsDemoMode],
  (listings, orgListings, userId, isDemoMode) => {
    const myListings = listings
      .filter((listing) => {
        // In demo mode, include demo buyer listings regardless of userId
        if (isDemoMode && listing.id?.startsWith('demo-listing-')) {
          return listing.type === 'BUYER';
        }
        return listing.userId === userId && listing.type === 'BUYER';
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    const organizationListings = orgListings
      .filter((listing) => listing.type === 'BUYER')
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    return [...myListings, ...organizationListings].filter(
      (listing, index, self) =>
        index === self.findIndex((t) => t.id === listing.id)
    );
  }
);

export const selectMySellingListings = createSelector(
  [selectListings, selectOrgListings, selectUserId, selectIsDemoMode],
  (listings, orgListings, userId, isDemoMode) => {
    const myListings = listings
      .filter((listing) => {
        // In demo mode, include demo seller listings regardless of userId
        if (isDemoMode && listing.id?.startsWith('demo-seller-')) {
          return listing.type === 'SELLER';
        }
        return listing.userId === userId && listing.type === 'SELLER';
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    const organizationListings = orgListings
      .filter((listing) => listing.type === 'SELLER')
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    return [...myListings, ...organizationListings].filter(
      (listing, index, self) =>
        index === self.findIndex((t) => t.id === listing.id)
    );
  }
);

export const selectMyApprovedListings = createSelector(
  [selectMySellingListings],
  (listings) =>
    listings.filter((listing) => listing.status === ListingStatus.APPROVED)
);

export const selectInvolvedListings = createSelector(
  [selectSellingOffers, selectSellingShowings, selectMessages],
  (offers, showings, messages) => {
    // Extract listing IDs from each source
    const offerListingIds = offers.map((offer) => offer.listingId);
    const showingListingIds = showings.map((showing) => showing.listingId);
    const messageListingIds = messages.map((message) => message.listingId);

    // Combine all IDs and remove duplicates
    const allInvolvedListingIds = _.uniq([
      ...offerListingIds,
      ...showingListingIds,
      ...messageListingIds,
    ]);

    return allInvolvedListingIds;
  }
);

// New selector to get listing status
export const selectListingStatus = createSelector(
  [selectListings],
  (listings) =>
    (listingId: string): PreListingStatusData | undefined => {
      const listing = listings.find((listing) => listing.id === listingId);
      if (!listing) return;

      // Listing step completed when past DRAFT (PRICE_PENDING, PENDING, or APPROVED)
      const listingCompleted = listing.status !== ListingStatus.DRAFT;

      // Photos logic - based on photoDate
      const photosPending =
        listingCompleted &&
        listing.photoDate &&
        isBefore(new Date(), new Date(listing.photoDate));
      const photosCompleted =
        listingCompleted &&
        listing.photoDate &&
        isAfter(new Date(), new Date(listing.photoDate));

      // Price status logic - now based on pricingOpinion and status
      const hasPricingOpinion = !!listing.pricingOpinion;
      const hasApprovedPrice = !!listing.pricingOpinion?.approvedPrice;
      const isPricePending = listing.status === ListingStatus.PRICE_PENDING;
      const isAwaitingSignature = listing.status === ListingStatus.AWAITING_SIGNATURE;

      // User needs to select price when: PRICE_PENDING and has pricing opinion but no approval
      const needsUserPricing = isPricePending && hasPricingOpinion && !hasApprovedPrice;
      // Price is pending when: PRICE_PENDING and waiting for Homepoint to create opinion
      const pricePending = isPricePending && !hasPricingOpinion;
      // Price completed when status is AWAITING_SIGNATURE or beyond (or has approved price)
      const priceCompleted = isAwaitingSignature || hasApprovedPrice ||
        listing.status === ListingStatus.PENDING ||
        listing.status === ListingStatus.APPROVED;

      // Signing status - completed when status is PENDING or APPROVED (post-signing)
      const signingCompleted =
        listing.status === ListingStatus.PENDING ||
        listing.status === ListingStatus.APPROVED;
      // Signing pending when status is AWAITING_SIGNATURE
      const signingPending = isAwaitingSignature;

      return {
        listing: {
          pending: listing.status === ListingStatus.DRAFT,
          completed: listingCompleted,
        },
        photos: {
          pending: !!photosPending,
          completed: !!photosCompleted,
        },
        price: {
          pending: !!pricePending,
          completed: !!priceCompleted,
          userPriced: hasApprovedPrice,
          homepointPriced: hasPricingOpinion,
          needsUserPricing: !!needsUserPricing,
        },
        signing: {
          pending: !!signingPending,
          completed: !!signingCompleted,
        },
        final: {
          pending: listing.status === ListingStatus.PENDING,
          completed: listing.status === ListingStatus.APPROVED,
        },
      };
    }
);
